// Document screen (the `document` step). Twin of the desktop DocumentViewerPage.
// Shows a researcher-uploaded file (PDF or DOCX) fetched over the session, with
// an optional minimum-read gate on Continue and an optional auto-end timer.
//
// Rendering (parity with document_viewer_page.py):
//   - PDF  -> pdf.js renders each page to a canvas on a dark viewer background
//   - DOCX -> mammoth converts to HTML, shown on a white page (same engine the
//             desktop uses, so the Word view matches in-lab)
// Both libraries are dynamically imported so they only load for document blocks.
//
// Timers:
//   - end_condition "timed"/"both": auto-end after duration_seconds -> timed_auto
//   - show_countdown: "M:SS remaining" ticking down the duration
//   - min_read_seconds: Continue disabled until it elapses
//   - load failure: show the error, then auto-continue after 5s -> load_error
//
// Submit payload: { document_media_id, document_filename, document_extension,
//                   time_on_block_seconds, end_trigger }.

import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import type { WebRendererProps } from "./registry";
import type {
  DocumentContent, DocumentResult, DocMidBlockSurvey, VideoSurveyResponse,
} from "../api/types";
import { fetchDocument, fetchDocumentFile } from "../session/sessionDriver";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";
// Static URL import for the pdf.js worker (Vite resolves this to a hashed asset).
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

const ERROR_AUTO_CONTINUE_MS = 5000; // matches desktop _ERROR_AUTO_CONTINUE_MS
const PDF_SCALE = 1.5;

export function formatRemaining(totalSeconds: number): string {
  const remaining = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")} remaining`;
}

type Phase = "loading" | "viewing" | "error";

export default function DocumentRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;
  const title = typeof settings.title === "string" ? settings.title : "";
  const instructions = typeof settings.instructions === "string" ? settings.instructions : "";
  const endCondition = (settings.end_condition as string) ?? "continue";
  const durationSeconds =
    typeof settings.duration_seconds === "number" ? settings.duration_seconds : 600;
  const minReadSeconds =
    typeof settings.min_read_seconds === "number" ? settings.min_read_seconds : 0;
  const showCountdown =
    Boolean(settings.show_countdown) && (endCondition === "timed" || endCondition === "both");
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMsg, setErrorMsg] = useState("Loading document...");
  const [ext, setExt] = useState<string>("");
  const [continueEnabled, setContinueEnabled] = useState(!(minReadSeconds > 0));
  const [submitted, setSubmitted] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(showCountdown ? durationSeconds : null);
  const [activeSurvey, setActiveSurvey] = useState<DocMidBlockSurvey | null>(null);

  const metaRef = useRef<DocumentContent | null>(null);
  const startRef = useRef<number>(Date.now());
  const lockedRef = useRef(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<number[]>([]);
  // Mid-block survey state (mirrors VideoRenderer): fired-once tracking, the
  // in-flight survey that gates + defers any block-end, and captured responses.
  const triggersRef = useRef<DocMidBlockSurvey[]>([]);
  const firedRef = useRef<Set<number>>(new Set());
  const awaitingSurveyRef = useRef(false);
  const pendingEndRef = useRef<DocumentResult["end_trigger"] | null>(null);
  const surveyResponsesRef = useRef<VideoSurveyResponse[]>([]);
  const activeSurveyRef = useRef<DocMidBlockSurvey | null>(null);

  function complete(endTrigger: DocumentResult["end_trigger"]) {
    // Never end out from under an open mid-block survey; honor it on close.
    if (awaitingSurveyRef.current) {
      pendingEndRef.current = endTrigger;
      return;
    }
    if (lockedRef.current) return; // re-entrance guard
    lockedRef.current = true;
    timersRef.current.forEach((t) => window.clearInterval(t));
    timersRef.current.forEach((t) => window.clearTimeout(t));
    if (endTrigger === "continue") setSubmitted(true);
    const meta = metaRef.current;
    const elapsed = (Date.now() - startRef.current) / 1000;
    const result: DocumentResult = {
      document_media_id: meta?.document_media_id ?? null,
      document_filename: meta?.document_filename ?? "",
      document_extension: meta?.document_extension ?? "",
      time_on_block_seconds: Math.round(elapsed * 1000) / 1000,
      end_trigger: endTrigger,
      survey_responses: surveyResponsesRef.current,
    };
    const submit = () => onDone({ kind: "submit", result });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  // Fire the earliest not-yet-fired time trigger the elapsed clock has reached,
  // one at a time. The survey overlays the document; the block-end (if any) is
  // deferred until it closes. Mirrors VideoRenderer's mid-video survey logic.
  function checkSurveyTriggers(elapsedSeconds: number) {
    if (awaitingSurveyRef.current || lockedRef.current) return;
    const list = triggersRef.current;
    for (let i = 0; i < list.length; i++) {
      if (firedRef.current.has(i)) continue;
      if (elapsedSeconds >= list[i].value) {
        firedRef.current.add(i);
        fireSurvey(list[i]);
        return;
      }
      break; // value-sorted: the first unreached trigger ends the scan
    }
  }

  function fireSurvey(trigger: DocMidBlockSurvey) {
    awaitingSurveyRef.current = true;
    activeSurveyRef.current = trigger;
    setActiveSurvey(trigger);
  }

  function onSurveySubmit(responses: SurveyResponse[]) {
    const trigger = activeSurveyRef.current;
    if (trigger) {
      surveyResponsesRef.current.push({
        survey_id: trigger.survey_id,
        survey_name: trigger.survey_name,
        responses,
      });
    }
    activeSurveyRef.current = null;
    setActiveSurvey(null);
    awaitingSurveyRef.current = false;
    // Honor a block-end that came due while the survey was open.
    if (pendingEndRef.current) {
      const t = pendingEndRef.current;
      pendingEndRef.current = null;
      complete(t);
    }
  }

  function showError(message: string) {
    setPhase("error");
    setErrorMsg(message);
    const t = window.setTimeout(() => complete("load_error"), ERROR_AUTO_CONTINUE_MS);
    timersRef.current.push(t);
  }

  // Load + render the document.
  useEffect(() => {
    let alive = true;
    startRef.current = Date.now();
    (async () => {
      try {
        const meta = await fetchDocument(session);
        if (!alive) return;
        metaRef.current = meta;
        // Arm mid-block surveys: sorted by threshold, polled against elapsed
        // seconds. (Triggers load with the doc metadata, so the poll starts here.)
        triggersRef.current = [...(meta.mid_block_surveys ?? [])].sort(
          (a, b) => a.value - b.value,
        );
        if (triggersRef.current.length > 0) {
          const iv = window.setInterval(() => {
            checkSurveyTriggers((Date.now() - startRef.current) / 1000);
          }, 500);
          timersRef.current.push(iv);
        }
        const extension = (meta.document_extension || "").toLowerCase();
        setExt(extension);
        if (!meta.document_media_id) {
          showError("This document is not available.");
          return;
        }
        const buf = await fetchDocumentFile(session);
        if (!alive) return;

        if (extension === "pdf") {
          const pdfjs = await import("pdfjs-dist");
          pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
          const pdf = await pdfjs.getDocument({ data: buf }).promise;
          if (!alive) return;
          setPhase("viewing");
          const host = viewerRef.current;
          if (host) host.innerHTML = "";
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            if (!alive) return;
            const viewport = page.getViewport({ scale: PDF_SCALE });
            const canvas = document.createElement("canvas");
            canvas.className = "jol-doc-page";
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");
            if (ctx && viewerRef.current) {
              viewerRef.current.appendChild(canvas);
              await page.render({ canvasContext: ctx, viewport }).promise;
            }
          }
        } else if (extension === "docx") {
          const mammoth = await import("mammoth");
          const result = await mammoth.convertToHtml({ arrayBuffer: buf });
          if (!alive) return;
          setPhase("viewing");
          if (viewerRef.current) {
            const page = document.createElement("div");
            page.className = "jol-doc-docx-page";
            // mammoth is a converter, not a sanitizer (a DOCX javascript: hyperlink
            // would land live). Sanitize like Consent/Debrief; DOMPurify's default
            // keeps headings/tables/formatting/data: images and strips scripts and
            // javascript: hrefs.
            page.innerHTML = DOMPurify.sanitize(result.value);
            viewerRef.current.innerHTML = "";
            viewerRef.current.appendChild(page);
          }
        } else {
          showError("This document type cannot be displayed.");
        }
      } catch {
        if (alive) showError("The document could not be loaded.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [session]);

  // Timers: auto-end (+ countdown) and the minimum-read gate.
  useEffect(() => {
    if (endCondition === "timed" || endCondition === "both") {
      const end = window.setTimeout(() => complete("timed_auto"), durationSeconds * 1000);
      timersRef.current.push(end);
      if (showCountdown) {
        const tick = window.setInterval(() => {
          const left = durationSeconds - (Date.now() - startRef.current) / 1000;
          setRemaining(Math.max(0, left));
        }, 1000);
        timersRef.current.push(tick);
      }
    }
    if (minReadSeconds > 0) {
      const gate = window.setTimeout(() => setContinueEnabled(true), minReadSeconds * 1000);
      timersRef.current.push(gate);
    }
    return () => {
      timersRef.current.forEach((t) => window.clearInterval(t));
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewerClass =
    "jol-doc-viewer " + (ext === "docx" ? "jol-doc-viewer--docx" : "jol-doc-viewer--pdf");

  return (
    <div className="jol-doc">
      {title.trim() ? <div className="jol-doc-title">{title}</div> : null}
      {instructions.trim() ? <div className="jol-doc-instructions">{instructions}</div> : null}

      {phase === "loading" ? (
        <div className="jol-doc-viewer jol-doc-viewer--pdf">
          <div className="jol-doc-loading">Loading document...</div>
        </div>
      ) : phase === "error" ? (
        <div className="jol-doc-viewer jol-doc-viewer--pdf">
          <div className="jol-doc-error">{errorMsg}</div>
        </div>
      ) : (
        <div ref={viewerRef} className={viewerClass} />
      )}

      <div className="jol-doc-footer">
        <span className="jol-doc-countdown">
          {remaining !== null ? formatRemaining(remaining) : ""}
        </span>
        <button
          type="button"
          className="jol-doc-continue"
          disabled={!continueEnabled || submitted || phase === "error"}
          onClick={() => complete("continue")}
        >
          {submitted ? "Continuing..." : "Continue"}
        </button>
      </div>

      {activeSurvey ? (
        <div className="jol-doc-survey-overlay">
          <SurveyForm
            questions={activeSurvey.questions}
            submitLabel="Submit"
            onSubmit={onSurveySubmit}
          />
        </div>
      ) : null}
    </div>
  );
}
